<template>
    <div class="container-fluid content-wrapper">
        <div class="content-heading">AI Reports</div>

        <!-- The question -->
        <b-row>
            <b-col cols="12" md="9">
                <p-input
                    label="What do you want to know?"
                    name="question"
                    v-model="question"
                    placeholder="e.g. Transactions over $500 last month"
                    @keydown.enter.prevent="ask" />
            </b-col>
            <!-- No margin on the button: p-button applies a class to its wrapper and the button
                 both, so mb-3 doubled up and lifted it level with the label. The input has no
                 bottom margin, so aligning to the column's end lines the two up. -->
            <b-col cols="12" md="3" class="d-flex align-items-end">
                <p-button variant="primary" :is-busy="isAsking" :disabled="!question.trim()" @click="ask">Generate report</p-button>
            </b-col>
        </b-row>

        <div class="small text-muted mb-2">
            Ask in plain language. The AI only works out which report you mean and its settings; the
            numbers always come from Mosaic.
            <a v-if="exampleQuestions.length" href="#" @click.prevent="showExamples = !showExamples">
                {{ showExamples ? 'Hide' : 'See' }} example questions
            </a>
        </div>
        <div v-if="showExamples" class="examples mb-2">
            <button v-for="q in exampleQuestions" :key="q" class="example-chip" @click="useExample(q)">{{ q }}</button>
        </div>

        <!-- The model could not be reached. Say so; a guess would be worse. -->
        <p-card v-if="state === 'unavailable'" title="I can't interpret questions right now" class="mt-2">
            <p class="mb-0">{{ message }}</p>
        </p-card>

        <!-- Not understood. Never guess: show what can be asked. -->
        <p-card v-if="state === 'unmatched'" title="I don't understand that yet" class="mt-2">
            <p class="mb-2">
                I can't turn that into a report. Rather than guess at what you meant, here is what I
                can answer today:
            </p>
            <p v-if="unmatchedReason" class="small text-muted mb-2">{{ unmatchedReason }}</p>
            <ul class="mb-0">
                <li v-for="q in exampleQuestions" :key="q">
                    <a href="#" @click.prevent="useExample(q)">{{ q }}</a>
                </li>
            </ul>
        </p-card>

        <!-- What it understood. The sentence is built by the server from the settings that
             will run, so it cannot say one thing and run another. -->
        <p-card v-if="agent" title="I understood you to ask" class="mt-2">
            <p class="mb-2 fst-italic">{{ restatement }}</p>

            <div v-if="agent.assumptions.length" class="mt-2">
                <strong class="small">Assumptions I made:</strong>
                <ul class="small mb-0">
                    <li v-for="a in agent.assumptions" :key="a">{{ a }}</li>
                </ul>
            </div>

            <hr class="my-3" />
            <h6>Settings</h6>
            <TransactionsParameters
                v-if="tool"
                v-model="params"
                :tool="tool"
                :errors="errors"
                :ambiguities="ambiguities"
                :busy="isBusy"
                :dirty="dirty"
                @run="run(1)" />
        </p-card>

        <!-- The rows, filtered and paged in SQL. -->
        <ServerReportResults
            v-if="agent && applied"
            :title="resultsTitle"
            :rows="rows"
            :columns="columns"
            :total="total"
            :page="page"
            :per-page="perPage"
            :busy="isBusy"
            @page="run($event)"
            @per-page="changePerPage($event)"
            @sort="sort($event)"
            @export="exportRows($event)" />

        <b-row v-if="agent && applied" class="mt-3">
            <b-col cols="12" md="8">
                <p-input label="Save this report as" name="reportName" v-model="reportName" />
            </b-col>
            <b-col cols="12" md="4" class="d-flex align-items-end">
                <p-button variant="primary" :disabled="!reportName.trim() || dirty" :is-busy="isSaving" @click="save">Save report</p-button>
            </b-col>
        </b-row>

        <!-- Saved AI reports and the shared activity log. -->
        <b-tabs content-class="mt-3" class="mt-4">
            <b-tab title="Saved AI reports">
                <p-card>
                    <p-table :items="savedReports" :fields="savedFields" :per-page="25" show-per-page-selector :enable-row-selection="false">
                        <template v-slot:cell(name)="{ item }">
                            <router-link :to="`/reports/ai/${item.id}`">{{ item.name }}</router-link>
                        </template>
                        <template v-slot:cell(ownerName)="{ item }">{{ item.ownerName }}</template>
                        <template v-slot:cell(modifiedOn)="{ item }">{{ when(item.modifiedOn) }}</template>
                        <!-- A shared report can only be deleted by the person who saved it. -->
                        <template v-slot:cell(remove)="{ item }">
                            <p-button v-if="item.isMine" variant="outline-danger" size="sm" @click.stop="remove(item)">Delete</p-button>
                        </template>
                    </p-table>
                    <p v-if="store.loaded && !savedReports.length" class="mb-0 text-muted">No saved AI reports yet.</p>
                </p-card>
            </b-tab>
            <b-tab title="Activity" lazy>
                <ReportActivity />
            </b-tab>
        </b-tabs>
    </div>
</template>

<script>
// AI Reports: ask a question in plain language, see what was understood, adjust the settings
// and run it. The model (reached only through reportagent/resolve) picks the report and its
// settings; everything after that runs on the server with no model involved. Saved AI
// reports are shared by everyone and kept apart from Report Templates / My Reports.
import { useAppStateStore } from '@/stores/appState';
import { useAiReportsStore } from '@/stores/aiReports';
import * as reportAgentApi from '@/services/reportAgentApi';
import { sameSettings } from '@/services/reportSettings';
import TransactionsParameters from '@/views/Reports/Agent/TransactionsParameters.vue';
import ServerReportResults from '@/views/Reports/Agent/ServerReportResults.vue';
import ReportActivity from '@/views/Reports/Agent/ReportActivity.vue';

const PER_PAGE = 25;
const UNAVAILABLE_MESSAGE = "I can't interpret questions right now. Try again shortly.";

export default {
    name: 'AiReports',
    components: { TransactionsParameters, ServerReportResults, ReportActivity },
    data() {
        return {
            question: '',
            showExamples: false,
            tools: [],
            state: 'idle', // idle | unavailable | unmatched | resolved
            message: '',
            unmatchedReason: '',
            agent: null,
            params: {},
            applied: null,
            restatement: '',
            errors: {},
            ambiguities: [],
            rows: [],
            columns: [],
            total: 0,
            page: 1,
            perPage: PER_PAGE,
            sortBy: null,
            sortDesc: false,
            reportName: '',
            isAsking: false,
            isBusy: false,
            isSaving: false,
            savedFields: [
                { key: 'name', label: 'Report', sortable: true },
                { key: 'question', label: 'Asked' },
                { key: 'ownerName', label: 'Saved by', sortable: true },
                { key: 'modifiedOn', label: 'Changed', sortable: true },
                { key: 'remove', label: '', class: 'tcw100' },
            ],
        };
    },
    computed: {
        store() {
            return useAiReportsStore();
        },
        savedReports() {
            return this.store.reports;
        },
        locationId() {
            return useAppStateStore().getSelectedLocationId;
        },
        exampleQuestions() {
            return [...new Set(this.tools.flatMap(x => x.exampleQuestions || []))];
        },
        tool() {
            return this.agent ? this.tools.find(x => x.id === this.agent.toolId) || null : null;
        },
        resultsTitle() {
            const name = this.tool ? this.tool.name : 'Report';
            return `${name} (${this.total} ${this.total === 1 ? 'row' : 'rows'})`;
        },
        // Edited but not run: what is on screen came from the previous settings.
        dirty() {
            return !!this.applied && !sameSettings(this.applied, this.params);
        },
    },
    async created() {
        this.store.load();
        try {
            this.tools = await reportAgentApi.getTools();
        } catch {
            this.tools = [];
        }
    },
    methods: {
        clear() {
            this.state = 'idle';
            this.message = '';
            this.unmatchedReason = '';
            this.agent = null;
            this.params = {};
            this.applied = null;
            this.restatement = '';
            this.errors = {};
            this.ambiguities = [];
            this.rows = [];
            this.columns = [];
            this.total = 0;
            this.page = 1;
            this.sortBy = null;
            this.sortDesc = false;
        },
        useExample(q) {
            this.question = q;
            this.ask();
        },
        async ask() {
            const question = this.question.trim();
            if (!question)
                return;

            this.clear();
            this.isAsking = true;
            let result = null;
            try {
                result = await reportAgentApi.resolve(question, this.locationId);
            } catch {
                result = null;
            } finally {
                this.isAsking = false;
            }

            if (!result || result.status === 'unavailable') {
                this.message = (result && result.message) || UNAVAILABLE_MESSAGE;
                this.state = 'unavailable';
                return;
            }

            if (result.status !== 'resolved') {
                this.unmatchedReason = result.reason || '';
                this.state = 'unmatched';
                return;
            }

            // The server keeps an ambiguous or unmatched name as the name, so the settings
            // offer its choices or its error, and a run cannot quietly drop it.
            this.agent = result;
            this.params = { ...(result.parameters || {}) };
            this.restatement = result.restatement;
            this.errors = result.errors || {};
            this.ambiguities = result.ambiguities || [];
            this.reportName = question.replace(/\?+$/, '').trim();
            this.state = 'resolved';

            // Something to settle first (which Sarah? no such register): wait for the person.
            if (Object.keys(this.errors).length || this.ambiguities.length)
                return;

            await this.run(1);
        },
        // Run, re-run after an edit, page and sort all come through here; the model is not involved.
        async run(page) {
            this.isBusy = true;
            try {
                const data = await reportAgentApi.run({
                    toolId: this.agent.toolId,
                    parameters: this.params,
                    locationId: this.locationId,
                    currentPage: page,
                    perPage: this.perPage,
                    sortBy: this.sortBy,
                    sortDesc: this.sortDesc,
                });
                this.rows = data.rows;
                this.columns = data.columns;
                this.total = data.totalRows;
                this.restatement = data.restatement;
                this.page = page;
                this.errors = {};
                this.ambiguities = [];
                this.applied = JSON.parse(JSON.stringify(this.params));
            } catch (e) {
                // The global handler already said what is wrong; show it against each setting too.
                const validation = e.response && e.response.data && e.response.data.validation;
                if (validation) {
                    this.errors = validation.errors || {};
                    this.ambiguities = validation.ambiguities || [];
                }
            } finally {
                this.isBusy = false;
            }
        },
        // A new page size starts again from the first page.
        changePerPage(perPage) {
            this.perPage = perPage;
            this.run(1);
        },
        sort({ sortBy, sortDesc }) {
            this.sortBy = sortBy;
            this.sortDesc = sortDesc;
            this.run(1);
        },
        async exportRows(format) {
            try {
                await reportAgentApi.exportReport({
                    toolId: this.agent.toolId,
                    parameters: this.applied,
                    locationId: this.locationId,
                    format,
                });
            } catch {
                // The global handler shows the server's message (for example, PDF over its row limit).
            }
        },
        async save() {
            this.isSaving = true;
            try {
                const saved = await reportAgentApi.saveReport({
                    name: this.reportName.trim(),
                    question: this.question.trim(),
                    toolId: this.agent.toolId,
                    parameters: this.applied,
                    locationId: this.locationId,
                });
                await this.store.load();
                this.$appSuccess && this.$appSuccess(`Saved "${saved.name}".`);
                this.$router.push(`/reports/ai/${saved.id}`);
            } catch {
                // The global handler shows why.
            } finally {
                this.isSaving = false;
            }
        },
        async remove(item) {
            try {
                await this.store.remove(item.id);
                this.$appSuccess && this.$appSuccess(`Report "${item.name}" deleted.`);
            } catch {
                // The global handler shows why.
            }
        },
        when(iso) {
            return iso ? new Date(iso).toLocaleString() : '';
        },
    },
};
</script>

<style scoped>
.examples { display: flex; flex-wrap: wrap; gap: 8px; }
.example-chip {
    background: #fff; border: 1px solid #e2e6e9; border-radius: 20px;
    padding: 6px 14px; font-size: 12.5px; color: #5b6a72; cursor: pointer;
}
.example-chip:hover { border-color: #33688f; color: #33688f; }
</style>
